// PARAQTA — Кітап беті (#pg-bookpage) — public book page with tabs.
// Module-internal state: _bpRating (replace _newRating).
// data-action атрибуттар → events.js delegation handlers арқылы шақырылады.
//
// Step BP1.1: Hero (Apple Books-стиль, центрленген мұқаба) +
// сипаттама блогы ("Толық оқу" toggle) + "Қазір оқып жатыр" (initials
// avatar overlap, gold/brown ротация) + "Бірге парақтайық." footer.

import { ugcBtn } from '../ugc/menu.js';
import { api } from '../../core/api.js';
import { track } from '../../core/track.js';
import { esc, fmtDate } from '../../utils/format.js';
import { coverBox } from '../../utils/covers.js';
import { toast } from '../../utils/toast.js';
import { show } from '../../core/router.js';

let _bpRating = 0;

// Stage 7.6: Күндік мақсат stepper — books.daily_str-ге жазады (ботпен синхрон).
let _goalSaveTimer = null;
export async function bpGoalStep(bookId, delta) {
  const valEl = document.querySelector('#pg-bookpage [data-field="bp-goal-val"]');
  if (!valEl) return;
  const cur = parseInt(valEl.textContent, 10) || 0;
  const next = Math.max(0, Math.min(1000, cur + delta));
  if (next === cur) return;
  valEl.textContent = next; // optimistic
  // Debounce — тез басқанда соңғы мәнді ғана жібереміз
  if (_goalSaveTimer) clearTimeout(_goalSaveTimer);
  _goalSaveTimer = setTimeout(async () => {
    try {
      const r = await fetch('/api/books/daily-goal', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ book_id: Number(bookId), pages: parseInt(valEl.textContent, 10) || 0 }),
      });
      if (!r.ok) { toast('⚠️ Сақталмады'); return; }
      toast('✓ Күндік мақсат сақталды');
    } catch (e) {
      toast('Желі қатесі');
    }
  }, 500);
}

// Stage 7.6: Жалпы бет санын сақтау (books.pages — бот үшін міндетті).
export async function bpSavePages(bookId) {
  const inp = document.querySelector('#pg-bookpage [data-field="bp-pages-input"]');
  if (!inp) return;
  const pages = parseInt(inp.value, 10) || 0;
  if (pages < 1 || pages > 50000) { toast('1-50000 бет аралығында енгіз'); return; }
  try {
    const r = await fetch('/api/books/total-pages', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ book_id: Number(bookId), pages }),
    });
    if (!r.ok) { toast('⚠️ Сақталмады'); return; }
    toast('✓ ' + pages + ' бет сақталды · бот ашатын болды');
    // Re-render to drop the amber warning
    const slug = (location.hash || '').replace(/^#book\//, '');
    if (slug && typeof window.openBookPage === 'function') {
      setTimeout(() => window.openBookPage(slug), 600);
    }
  } catch (e) {
    toast('Желі қатесі');
  }
}

// Step BP1.1: description preview-нің ұзындығы. 200+ char → 160 chars
// preview + "Толық оқу →". Толық тіркесі sentence boundary-да емес,
// қарапайым slice — UX үшін жеткілікті.
const DESC_PREVIEW_LIMIT = 200;
const DESC_PREVIEW_LEN = 160;

// "Қазір оқып жатыр" аватарларының реттік түс жұптары —
// бренд палитра (gold C9A96E + brown 6B4423/8B5E34/A07A52).
const READING_AVATAR_COLORS = [
  { bg: '#C9A96E', fg: '#1C1A16' },
  { bg: '#8B5E34', fg: '#F7F2EA' },
  { bg: '#6B4423', fg: '#F7F2EA' },
  { bg: '#A07A52', fg: '#F7F2EA' },
  { bg: '#C9A96E', fg: '#1C1A16' },
];

function getInitials(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
}

function renderReadingNowAvatars(names) {
  if (!names || !names.length) return '';
  const visible = names.slice(0, 5);
  const extra = names.length - visible.length;
  const avatars = visible.map((n, i) => {
    const c = READING_AVATAR_COLORS[i % READING_AVATAR_COLORS.length];
    return '<span class="bp-avatar" style="background:' + c.bg + ';color:' + c.fg + '" title="' + esc(n) + '">' +
      esc(getInitials(n)) + '</span>';
  }).join('');
  const more = extra > 0 ? '<span class="bp-avatar bp-avatar-more">+' + extra + '</span>' : '';
  return avatars + more;
}

// Step BP1.1: Hero — мұқаба үлкен центрленген (Apple Books).
// CTA: 2 батырма (login-да оқу тізімі статустары, әйтпесе 1 кіру).
// Share batырмалар (WA/TG/Көшіру) BP1.2-ке дейін кетті — UI clean кезді.
function renderBPHero(d, info, isLoggedIn) {
  // Step LH1: бренд placeholder — coverBox() ортақ helper.
  // cover_url жоқ → cov-pq brown gradient + .cover-label (Cormorant
  // italic title + gold line + uppercase author).
  // Платформалық қордағы кітаптың НАҒЫЗ мұқабасы (EPUB ішінен) басым —
  // Google-дан келген суреттерде қазақша кітаптарда қате мұқаба жиі кездеседі.
  const lib = d.library || null;
  const cover = coverBox(d.title, d.author, (lib && lib.cover_url) || info.cover_url);

  const metaBits = [];
  if (d.genre && d.genre.name_kk) metaBits.push(esc(d.genre.name_kk));
  if (info.year) metaBits.push(String(info.year));
  if (d.pages) metaBits.push(d.pages + ' бет');
  const metaLine = metaBits.length
    ? '<div class="book-meta-hero">' + metaBits.join(' · ') + '</div>'
    : '';

  const ratingBlock = d.avg_rating
    ? '<div class="book-rating-hero">' +
        '<span class="bp-rating-stars">' +
          '★'.repeat(Math.round(d.avg_rating)) +
          '☆'.repeat(5 - Math.round(d.avg_rating)) +
        '</span>' +
        '<span class="bp-rating-num">' + d.avg_rating + '</span>' +
        '<span class="bp-rating-sep">·</span>' +
        '<span class="bp-rating-count">' + (d.review_count || 0) + ' пікір</span>' +
      '</div>'
    : '';

  const bpT = esc(d.title);
  const bpA = esc(d.author || '');
  const bpPg = d.pages || 0;

  // Stage 7.5: status mapping бот pattern-мен сай: 0=сақтап қойдым, 1=оқып жатыр, 2=аяқтадым.
  // Тіркелмегендерге — бір "Кіру арқылы сақтау" pill. Тіркелгендерде —
  // 3 outline pill, белсенді статусы filled brown. Қайта басу → toggle off.
  let cta;
  if (isLoggedIn) {
    const us = d.user_status;
    const btn = (status, label) => {
      const active = us === status;
      const cls = 'book-status-btn' + (active ? ' active' : '');
      const action = active ? 'unlist-book' : 'save-book';
      return '<button class="' + cls + '" data-action="' + action +
        '" data-title="' + bpT + '" data-author="' + bpA +
        '" data-pages="' + bpPg + '" data-status="' + status + '">' + label + '</button>';
    };
    cta = btn(0, 'Оқуға сақтап қойдым') + btn(1, 'Оқып жатырмын') + btn(2, 'Аяқтадым');
  } else {
    cta = '<button class="book-cta-primary" data-action="show-login">Кіру арқылы сақтау</button>';
  }

  const ctaWrapClass = isLoggedIn ? 'book-status-row' : 'book-cta-row';

  // Stage 7.6: Күндік мақсат — "Оқып жатырмын" (status=1) кезінде ғана.
  // books.daily_str-ге жазады (ботпен синхрон).
  let goalControl = '';
  if (isLoggedIn && d.user_status === 1 && d.user_book_id) {
    const goal = Number(d.user_daily_goal) || 0;
    const bid = d.user_book_id;
    goalControl =
      '<div style="margin-top:14px;max-width:340px;margin-left:auto;margin-right:auto;padding:14px 16px;background:var(--card);border:0.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;">' +
        '<div style="display:flex;align-items:center;justify-content:center;gap:7px;margin-bottom:10px;">' +
          '<span style="font-size:15px;">🎯</span>' +
          '<span style="font-size:12px;font-weight:600;color:var(--ink);">Күндік мақсат</span>' +
        '</div>' +
        '<div style="display:flex;align-items:center;justify-content:center;gap:16px;">' +
          '<button data-action="bp-goal-step" data-book-id="' + bid + '" data-delta="-5" style="width:38px;height:38px;border-radius:50%;border:1px solid color-mix(in srgb,var(--ink) 12%,transparent);background:var(--card);font-size:20px;color:var(--ac);cursor:pointer;display:flex;align-items:center;justify-content:center;">−</button>' +
          '<div style="text-align:center;min-width:70px;">' +
            '<div data-field="bp-goal-val" style="font-family:\'PT Serif\',Georgia,serif;font-size:30px;font-weight:700;color:var(--ac);line-height:1;">' + goal + '</div>' +
            '<div style="font-size:10px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;">бет / күн</div>' +
          '</div>' +
          '<button data-action="bp-goal-step" data-book-id="' + bid + '" data-delta="5" style="width:38px;height:38px;border-radius:50%;border:1px solid color-mix(in srgb,var(--ink) 12%,transparent);background:var(--card);font-size:20px;color:var(--ac);cursor:pointer;display:flex;align-items:center;justify-content:center;">+</button>' +
        '</div>' +
      '</div>';
  }

  // Stage 7.5 Bug 1: артқа батырма (top nav) + Share батырмасы (оң жақта)
  const bookSlug = (d.slug || '').trim();
  const shareUrl = bookSlug ? 'https://paraqta.kz/kitap/' + bookSlug : '';
  const shareBtn = shareUrl
    ? '<button class="book-share-btn" data-action="book-share" data-url="' + shareUrl +
      '" data-title="' + esc(d.title) + '" data-author="' + esc(d.author || '') +
      '" aria-label="Бөлісу" title="Бөлісу">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20">' +
      '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
      '<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>' +
      '</svg></button>'
    : '';
  const backNav = '<div class="book-back-nav">' +
    '<button class="book-back-btn" data-action="book-back" aria-label="Артқа">' +
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
    '</button>' +
    shareBtn +
    '</div>';

  // «Платформада оқи аласың» блогы — қордағы кітап үшін. Сөреге қосу да,
  // оқуды бастау да ОСЫ жерден (founder: беттен шықпай оқуға кірсін).
  let libBlock = '';
  if (lib) {
    libBlock =
      '<div class="bp-lib">' +
        '<div class="bp-lib-tag">📖 Платформада оқи аласың</div>' +
        '<div class="bp-lib-sub">Тегін, толық мәтін' +
          (lib.est_pages ? ' · ' + lib.est_pages + ' бет' : '') + '</div>' +
        '<button class="bp-lib-btn" data-action="lib-read" data-title="' + bpT +
          '" data-author="' + bpA + '">🕯 Сөреге қосып, оқуды бастау</button>' +
        (lib.source_note ? '<div class="bp-lib-src">' + esc(lib.source_note) + '</div>' : '') +
      '</div>';
  } else if (!/қара сөз/i.test(d.title || '')) {
    // 25.09: мәтіні ЖОҚ кітап. Адам «кітап осында ашылады» деп күтіп, шам
    // жағып, ештеңе ашылмаған соң кететін. Енді бірден шындығын айтып,
    // үш жол ұсынамыз: өз файлы · қағаз кітап · осындағы тегін кітаптар.
    const buy = info.buy_link
      ? '<a class="bp-own-buy" href="' + esc(info.buy_link) + '" target="_blank" rel="noopener">Қайдан сатып алуға болады →</a>'
      : '';
    // Қайталамау: қағаз кітап жолы = төмендегі «Оқып жатырмын» батырмасы,
    // «кітабым жоқ» жолы = астындағы «Мыналарды осында тегін оқи аласың»
    // бөлімі. Бұл блок тек шындықты айтады + жаңа жол (файл жүктеу) береді.
    libBlock =
      '<div class="bp-own">' +
        '<div class="bp-own-tag">✍️ Бұл кітапты өз кітабыңнан оқисың</div>' +
        '<div class="bp-own-sub">Мәтіні бізде жоқ — авторлық құқық қорғайды. Қағаз кітабың болса, «Оқып жатырмын» деп белгіле — PARAQTA оқығаныңды санап отырады.</div>' +
        '<button class="bp-own-opt main" data-action="bp-own-file"><span>📱</span><span><b>Файлым бар (EPUB, PDF)</b><small>Жүктеймін де, осында оқимын — тек өзім көремін</small></span></button>' +
        buy +
      '</div>';
  }

  return backNav + '<section class="book-hero">' +
    '<div class="book-cover-hero">' + cover + '</div>' +
    '<h1 class="book-title-hero">' + esc(d.title) + '</h1>' +
    (d.author ? '<div class="book-author-hero">' + esc(d.author) + '</div>' : '') +
    metaLine +
    ratingBlock +
    libBlock +
    '<div class="' + ctaWrapClass + '">' + cta + '</div>' +
    goalControl +
    '</section>';
}

function renderBPDesc(info) {
  const desc = (info.description || '').trim();
  if (!desc) return '';
  const isLong = desc.length > DESC_PREVIEW_LIMIT;
  const initial = isLong ? desc.slice(0, DESC_PREVIEW_LEN) + '…' : desc;
  // data-full атрибуты — toggle handler-да толық мәтінді restore үшін.
  const toggle = isLong
    ? '<button class="book-desc-toggle" data-action="toggle-desc">Толық оқу →</button>'
    : '';
  return '<section class="book-desc-block">' +
    '<p class="book-desc-text" id="bookDescText" data-full="' + esc(desc) + '">' + esc(initial) + '</p>' +
    toggle +
    '</section>';
}

// «Мұны тегін оқи аласың» — кітап бізде ЖОҚ кезде ғана шығады.
// Себебі (Search Console): кітап атауымен іздеп бетімізге жеткен адам
// ештеңе баспай кетеді — бетте оған ұсынатын ештеңе жоқ болатын.
function renderBPFreePicks(picks) {
  if (!picks || !picks.length) return '';
  let cards = '';
  picks.forEach(p => {
    const cov = p.cover_url
      ? '<img class="bp-fp-cov" src="' + esc(p.cover_url) + '" alt="" decoding="async">'
      : '<span class="bp-fp-cov bp-fp-cov-ph">📖</span>';
    cards +=
      '<button class="bp-fp-card" data-action="open-book" data-slug="' + esc(p.slug) + '">' +
        cov +
        '<span class="bp-fp-txt">' +
          '<span class="bp-fp-t">' + esc(p.title) + '</span>' +
          (p.author ? '<span class="bp-fp-a">' + esc(p.author) + '</span>' : '') +
          '<span class="bp-fp-p">' +
            (p.est_pages ? p.est_pages + ' бет · тегін' : 'тегін') +
          '</span>' +
        '</span>' +
      '</button>';
  });
  return '<section class="bp-fp">' +
    '<div class="bp-fp-head">Мыналарды осында тегін оқи аласың</div>' +
    '<div class="bp-fp-list">' + cards + '</div>' +
    '</section>';
}

function renderBPReadingNow(names) {
  if (!names || !names.length) return '';
  return '<section class="book-reading-now">' +
    '<div class="book-reading-now-head">' +
      '<span class="bp-rn-label">Қазір оқып жатыр</span>' +
      '<span class="bp-rn-count">' + names.length + ' адам</span>' +
    '</div>' +
    '<div class="book-reading-now-avatars">' +
      renderReadingNowAvatars(names) +
    '</div>' +
    '</section>';
}

export async function openBookPage(slug) {
  try { track('book', { slug }); } catch (_) {}
  if (window.isGuest) {
    document.getElementById('app').style.display = 'block';
    const _ls = document.getElementById('loginScreen') || document.getElementById('landingPage');
    if (_ls) _ls.style.display = 'none';
  }
  show('bookpage');
  location.hash = 'book/' + slug;
  const el = document.getElementById('bookPageContent');
  el.innerHTML = '<div class="loading"><span class="spinner"></span>Жүктелуде...</div>';

  // Enrich first
  fetch('/api/catalog/book/' + slug + '/enrich', { method: 'POST' }).catch(() => {});

  const d = await api('/api/catalog/book/' + encodeURIComponent(slug));
  if (!d.title) { el.innerHTML = '<div class="empty-state"><p>Кітап табылмады</p></div>'; return; }

  const info = d.info || {};
  const isLoggedIn = !!window.currentUser;

  // Step BP1.1: Hero + сипаттама + "Қазір оқып жатыр" — бөлек
  // helper-лерде. Tabs/reviews бөлімі BP1.2/1.3-ке тимей қалды.
  const bpT = esc(d.title);

  let html = '<div class="book-page">';
  html += renderBPHero(d, info, isLoggedIn);
  html += renderBPDesc(info);
  html += renderBPFreePicks(d.free_picks);
  html += renderBPReadingNow(d.reading_now);

  // Login CTA (guest-те hero CTA + бұл text-based CTA — дубль емес,
  // hero CTA "Кіру арқылы сақтау", бұл — "Рецензия/баға/прогрес үшін").
  if (!isLoggedIn) {
    html += '<div class="book-cta"><p>Рецензия жазу, баға қою, прогрес белгілеу үшін кіріңіз</p>' +
      '<button data-action="show-login">Telegram арқылы кіру</button></div>';
  }

  // Tabs (BP1.2-де redesign)
  const tabAttrs = 'data-title="' + bpT + '" data-slug="' + esc(slug) + '"';
  html += '<div class="book-tabs">' +
    '<button class="book-tab on" data-action="bp-tab" data-tab="reviews" ' + tabAttrs + '>Пікір (' + (d.review_count || 0) + ')</button>' +
    '<button class="book-tab" data-action="bp-tab" data-tab="quotes" ' + tabAttrs + '>Дәйексөз (' + (d.quotes?.length || 0) + ')</button>' +
    '<button class="book-tab" data-action="bp-tab" data-tab="discuss" ' + tabAttrs + '>Талқылау (' + (d.discussion_count || 0) + ')</button>' +
    (isLoggedIn ? '<button class="book-tab" data-action="bp-tab" data-tab="journal" ' + tabAttrs + '>Жеке жазба</button>' : '') +
    '</div>';
  html += '<div id="bpTabContent"></div>';

  // Footer — бренд жаны.
  html += '<footer class="book-page-footer">Бірге парақтайық.</footer>';
  html += '</div>'; // close book-page

  el.innerHTML = html;

  // Load reviews tab by default
  renderBPReviews(d.reviews || [], d.title, isLoggedIn);
}

// Step BP1.1: "Толық оқу" / "Қысқарту" toggle — events.js handler шақырады.
// data-full атрибутта толық мәтін бар, preview бастапқы render-де.
export function toggleBPDesc(btn) {
  const textEl = document.getElementById('bookDescText');
  if (!textEl) return;
  const fullDesc = textEl.dataset.full || '';
  if (textEl.classList.contains('expanded')) {
    textEl.textContent = fullDesc.slice(0, DESC_PREVIEW_LEN) + '…';
    textEl.classList.remove('expanded');
    btn.textContent = 'Толық оқу →';
  } else {
    textEl.textContent = fullDesc;
    textEl.classList.add('expanded');
    btn.textContent = 'Қысқарту ←';
  }
}

export function showBPTab(tab, btn, title, slug) {
  if (btn) { btn.closest('.book-tabs').querySelectorAll('.book-tab').forEach(b => b.classList.remove('on')); btn.classList.add('on'); }
  const el = document.getElementById('bpTabContent'); if (!el) return;
  const isLoggedIn = !!window.currentUser;

  if (tab === 'reviews') {
    el.innerHTML = '<div class="loading"><span class="spinner"></span></div>';
    api('/api/reviews/' + encodeURIComponent(title)).then(reviews => renderBPReviews(reviews, title, isLoggedIn));
  } else if (tab === 'quotes') {
    el.innerHTML = '<div class="loading"><span class="spinner"></span></div>';
    api('/api/quotes/' + encodeURIComponent(title)).then(quotes => {
      let h = '';
      if (isLoggedIn) {
        h += '<div style="margin-bottom:12px"><textarea class="jrnl" id="bpQuoteText" placeholder="Дәйексөз мәтінін енгізіңіз..." style="min-height:50px"></textarea>' +
          '<div style="display:flex;gap:6px;margin-top:6px;align-items:center"><input type="number" id="bpQuotePage" placeholder="Бет #" style="width:60px;padding:4px 6px;border:1px solid var(--bg3);border-radius:2px;font-size:.72rem;background:var(--bg);color:var(--ink)">' +
          '<button class="btn-sm" style="margin-left:auto" data-action="submit-quote" data-title="' + esc(title) + '">Қосу</button></div></div>';
      }
      h += quotes.length ? quotes.map(qq =>
        '<div class="quote-box" style="margin-bottom:8px"><div class="quote-text" style="font-size:.88rem">“' + esc(qq.text) + '”</div>' +
        '<div class="quote-src" style="display:flex;align-items:center;gap:6px">' + esc(qq.user_name || '') + (qq.page_num ? ' · ' + qq.page_num + '-бет' : '') + ugcBtn('quote', qq.id, qq.telegram_id) + '</div></div>'
      ).join('') : '<p style="font-size:.78rem;color:var(--ink4)">Әзірше дәйексөз жоқ</p>';
      el.innerHTML = h;
    });
  } else if (tab === 'discuss') {
    el.innerHTML = '<div class="loading"><span class="spinner"></span></div>';
    api('/api/discussions/' + encodeURIComponent(title)).then(msgs => {
      let h = '';
      if (isLoggedIn) {
        h += '<div style="display:flex;gap:6px;margin-bottom:12px"><input class="jrnl" id="bpDiscText" placeholder="Пікіріңді жаз..." style="min-height:auto;padding:6px 10px;flex:1">' +
          '<button class="btn-sm" data-action="submit-disc" data-title="' + esc(title) + '">Жіберу</button></div>';
      } else {
        h += '<div class="book-cta" style="margin-bottom:12px"><p>Талқылауға қатысу үшін кіріңіз</p>' +
          '<button data-action="show-login">Кіру</button></div>';
      }
      h += msgs.length ? msgs.map(m =>
        '<div class="msg' + (m.blurred ? ' blurred' : '') + '"><div class="msg-meta" style="display:flex;align-items:center;gap:6px">' + esc(m.user_name || '') + ' · ' + fmtDate(m.created_at) + ugcBtn('discussion', m.id, m.telegram_id) + '</div>' +
        '<div class="msg-body">' + esc(m.text) + '</div></div>'
      ).join('') : '<p style="font-size:.78rem;color:var(--ink4)">Талқылау басталмаған</p>';
      el.innerHTML = h;
    });
  } else if (tab === 'journal' && isLoggedIn) {
    el.innerHTML = '<div class="loading"><span class="spinner"></span></div>';
    api('/api/journal/' + encodeURIComponent(title)).then(j => {
      el.innerHTML = '<p style="font-size:.72rem;color:var(--ink4);margin-bottom:6px">🔒 Тек сен көресің</p>' +
        '<textarea class="jrnl" id="bpJournalText" placeholder="Еркін ойлар, жазба...">' + (j.text || '') + '</textarea>' +
        '<button class="btn-sm" style="margin-top:8px" data-action="submit-journal" data-title="' + esc(title) + '">Сақтау</button>';
    });
  }
}

export function renderBPReviews(reviews, title, isLoggedIn) {
  const el = document.getElementById('bpTabContent'); if (!el) return;
  let h = '';
  if (isLoggedIn) {
    h += '<div style="margin-bottom:14px">' +
      '<div style="display:flex;gap:6px;margin-bottom:8px"><span style="font-size:.8rem">Баға қой:</span>' +
      '<div class="stars" id="bpStars">' +
      [1,2,3,4,5].map(i => '<span data-s="' + i + '" data-hover-star="' + i + '" data-action="pick-star" data-star="' + i + '">&#9733;</span>').join('') + '</div></div>' +
      '<textarea class="jrnl" id="bpReviewText" placeholder="Пікіріңізді жазыңыз..." style="min-height:60px"></textarea>' +
      '<div style="display:flex;gap:6px;margin-top:6px"><label style="font-size:.68rem;color:var(--ink3)"><input type="checkbox" id="bpReviewSpoiler"> Спойлер бар</label>' +
      '<button class="btn-sm" style="margin-left:auto" data-action="submit-review" data-title="' + esc(title) + '">Жіберу</button></div></div>';
  } else {
    h += '<div class="book-cta" style="margin-bottom:14px"><p>Пікір жазу үшін кіріңіз</p>' +
      '<button data-action="show-login">Telegram арқылы кіру</button></div>';
  }
  h += reviews.length ? reviews.map(r =>
    '<div class="review-item"><div class="review-head"><span class="review-name">' + esc(r.user_name || '') + '</span>' +
    '<span class="review-stars">' + '★'.repeat(r.rating || 0) + '☆'.repeat(5 - (r.rating || 0)) + '</span>' +
    (r.spoiler ? '<span class="review-spoiler-tag">Спойлер</span>' : '') +
    '<span class="review-date">' + fmtDate(r.created_at) + '</span>' + ugcBtn('review', r.id, r.telegram_id) + '</div>' +
    '<div class="review-text' + (r.spoiler ? ' spoiler' : '') + '" data-action="spoiler-reveal">' +
      ((r.text && r.text.trim()) ? esc(r.text) : '<span style="opacity:.55;font-style:italic">Тек баға берді</span>') + '</div>' +
    '<button class="review-like' + (r._liked ? ' liked' : '') + '" data-action="like-review" data-review-id="' + r.id + '">❤ ' + (r.likes || 0) + ' пайдалы</button></div>'
  ).join('') : '<p style="font-size:.78rem;color:var(--ink4)">Әзірше пікір жоқ. Бірінші болып жаз!</p>';
  el.innerHTML = h;
}

export function hoverBPStar(n) { document.querySelectorAll('#bpStars span').forEach((s, i) => s.classList.toggle('on', i < n)); }
export function resetBPStars() { document.querySelectorAll('#bpStars span').forEach((s, i) => s.classList.toggle('on', i < _bpRating)); }
export function pickBPStar(n) { _bpRating = n; resetBPStars(); }

export async function submitBPReview(title) {
  const text = document.getElementById('bpReviewText')?.value;
  if (!text || text.length < 5) { toast('Пікір тым қысқа (кемінде 5 таңба)'); return; }
  await fetch('/api/reviews', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ book_title: title, rating: _bpRating, text, spoiler: document.getElementById('bpReviewSpoiler')?.checked ? 1 : 0 }) });
  toast('Пікір қосылды!'); _bpRating = 0;
  const reviews = await api('/api/reviews/' + encodeURIComponent(title));
  renderBPReviews(reviews, title, true);
}

export async function submitBPQuote(title) {
  const text = document.getElementById('bpQuoteText')?.value;
  if (!text) { toast('Дәйексөз енгізіңіз'); return; }
  const page = parseInt(document.getElementById('bpQuotePage')?.value) || null;
  const res = await fetch('/api/quotes', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ book_title: title, text, page_num: page }),
  });
  let data = null;
  try { data = await res.json(); } catch (_) {}
  if (data && data.status === 'limit_reached') {
    const { showPremiumLockModal } = await import('../premium-lock/modal.js');
    showPremiumLockModal('quote-limit', {
      extra: `Айлық ${data.used}/${data.limit} дәйексөз сақталды`,
    });
    return;
  }
  toast('Дәйексөз қосылды!');
  showBPTab('quotes', null, title);
}

export async function submitBPDisc(title) {
  const text = document.getElementById('bpDiscText')?.value;
  if (!text) { toast('Хабарлама енгізіңіз'); return; }
  await fetch('/api/discussions', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ book_title: title, text }) });
  toast('Жіберілді!'); showBPTab('discuss', null, title);
}

export async function submitBPJournal(title) {
  const text = document.getElementById('bpJournalText')?.value || '';
  await fetch('/api/journal', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ book_title: title, text }) });
  toast('Сақталды!');
}

export async function likeReview(reviewId, btn) {
  if (!window.currentUser) { toast('Лайк басу үшін кіріңіз'); return; }
  const r = await fetch('/api/reviews/' + reviewId + '/like', { method: 'POST', credentials: 'include' });
  if (r.ok) {
    const data = await r.json();
    btn.classList.toggle('liked', data.liked);
    const cnt = parseInt(btn.textContent.match(/\d+/)?.[0] || '0');
    btn.innerHTML = '❤ ' + (data.liked ? cnt + 1 : Math.max(0, cnt - 1)) + ' пайдалы';
  }
}
